const DEFAULT_CONCURRENCY = 4;

/**
 * Upload files with a fixed concurrency limit and byte-weighted overall progress.
 * Failed files do not abort the rest of the batch; they are returned so the
 * caller can retry them.
 */
export async function uploadWithConcurrency({
  files,
  uploadOne,
  concurrency = DEFAULT_CONCURRENCY,
  onProgress,
  onFileFailed,
  onFileSucceeded,
}) {
  if (!files.length) return { failed: [] };

  const fileProgress = new Array(files.length).fill(0);
  const fileSizes = files.map((f) => f.size || 0);
  const totalBytes = fileSizes.reduce((sum, size) => sum + size, 0) || files.length;

  let completedCount = 0;
  let nextIndex = 0;
  let inFlight = 0;
  const failed = [];

  const report = () => {
    let loadedBytes = 0;
    for (let i = 0; i < files.length; i++) {
      const size = fileSizes[i] || 1;
      loadedBytes += size * (fileProgress[i] / 100);
    }
    const percent = Math.min(100, Math.round((loadedBytes / totalBytes) * 100));
    onProgress?.({
      percent,
      completed: completedCount,
      total: files.length,
      active: inFlight,
      failed: failed.length,
    });
  };

  const worker = async () => {
    while (nextIndex < files.length) {
      const index = nextIndex++;
      const file = files[index];
      inFlight++;
      report();
      try {
        await uploadOne(file, (filePercent) => {
          fileProgress[index] = filePercent;
          report();
        });
        fileProgress[index] = 100;
        onFileSucceeded?.(file);
      } catch (error) {
        fileProgress[index] = 0;
        failed.push({ file, error });
        onFileFailed?.(file, error);
      } finally {
        inFlight--;
        completedCount++;
        report();
      }
    }
  };

  const workers = Array.from(
    { length: Math.min(concurrency, files.length) },
    () => worker()
  );
  await Promise.all(workers);
  return { failed };
}
